import { adapterPlanogramItemToPlanogramItemCommon } from "@CommonAdapters/pog";
import {
  PlanogramContainer,
  PlanogramContainerCommonNonCircular,
  UnrangedPlanogramContainer,
} from "./container";
import {
  type PlanogramItem,
  PlanogramItemCommonNonCircular,
  PlanogramItemNonCircular,
  UnrangedPlanogramItem,
  UnrangedPlanogramItemNonCircular,
  normalizePlanogramItemPlacementDefaults,
} from "./item";
import { Planogram, PlanogramNonCircular } from "./planogram";
import { PlanogramBay, PlanogramBayNonCircular } from "./bay";
import { PlanogramShelfNonCircular } from "./shelf";
import type { SplitPlan } from "@CommonTypes/algo/generated-pogger";
import {
  gradingOf,
  releaseAestheticsScore,
  type AestheticsGrading,
} from "@CommonUtils/cdtStudio/domain/pogAesthetics";
import type { SpineLevel } from "@CommonUtils/cdtStudio/domain/studioTypes";
import { AESTHETICS_VERSION } from "@CommonUtils/cdtStudio/domain/aestheticsRules";
import type {
  AestheticsScore,
  AestheticsGateId,
  ComponentKey,
  ComponentScore,
} from "@CommonUtils/cdtStudio/domain/aestheticsScore";

export const latestPogVersion = "4.7";
/**
 * 4.7 - Add splitAfter to bays (default false): the run physically breaks
 *       after this bay.
 * 4.6 - A stated maxFacingsRows (maxFacingsRowsStated) is the PSA merchYMax as
 *       the file states it. Pogs imported before stored it with the item's
 *       vertical caps taken off; the adapter adds yCapNum back.
 * 4.5 - Add loaderDepth to shelves (default null): depth a spring loader takes
 *       behind the stock (DEV-3518). Units deep falls back to the usable depth.
 *       With it, the loader rule's results, both default null: shelf
 *       `loaderDecision` (who decided the shelf's loader and on what counts)
 *       and item `loaderNeed`. `loaderDecision` also carries the pogger's
 *       decision when the request sent a `loader` block (optional `mode`,
 *       `active`, `needFacings`; absent on older 4.5 data, read as unknown),
 *       and item `unitsDeep` is then the pogger's `units_deep`. Also shelf
 *       `dividersKeptFromFixture` (default null): true when the keep switch
 *       kept the store fixture's dividers. And the pog's `horizontalRectangles`
 *       (absent: not sent, or an older pog): what the pogger's clean block
 *       shapes did on a horizontal-blocking store run (DEV-3531), with
 *       `brand_block_over_line_order` relaxations next to it.
 * 4.4 - Divider policy results, all default null: shelf `dividers.active` (the
 *       pogger's decision on a follow-products shelf), shelf `dividersSetByHand`
 *       and item `dividerNeed`.
 * 4.3 - Add dividers to shelves (default null). On pogs below 4.3, zero item
 *       dividerWidth/dividerHeight that equal displayWidth/displayHeight x100:
 *       the PSA product import stored the display size there (DEV-3516), which
 *       would become load-bearing once layout counts dividers.
 * 4.2 - Add pallet to bays and laneNo to shelves. Both are absent unless the
 *       bay is a floor pallet, so a pog without pallets reads as it did.
 * 4.1 - Normalize missing orientation/facings/facingsRows on legacy items.
 * 4.0 - Add nestingWidth/nestingHeight/nestingDepth to items (all default 0):
 *       PSA xNesting/yNesting/zNesting, stored for the round trip, not applied to geometry.
 * 3.10 - Add deadSpans to shelves (default []): the strips an obstruction
 *        blocks, so a pole can sit anywhere on a shelf, not only at its right end.
 * Add metrics.aesthetics (default null): the stored Aesthetics score, 0-100,
 *       null meaning the plan was never scored. No latestPogVersion bump of
 *       its own: the field is nullable and the adapter backfills it.
 *       metrics.aestheticsDetail (optional) records the scorer version, failed checks and
 *       graded components behind it, so a reader can trust and explain the score without
 *       the plan. Absent on a plan scored before it was stored.
 * 3.9 - Add effectiveWeightLimit to shelves (default null): the cap the last
 *       pogger run judged the shelf against, with its provenance. The ONLY
 *       source of a shelf limit — null means no verdict, never a default.
 * 3.8 - Add weight and weightUom to items (both default null). A STATED weight
 *       from a data source, never the derived one — see productWeight.ts.
 * 3.7 - Add unitsDeep to items and weightCapacity to shelves (both default null)
 * 3.6 - Add item capping properties (xCapNum/yCapNum/zCapNum and orientations).
 *       Shipped without a changelog line or a latestPogVersion bump; recorded
 *       here after the fact — the adapter block always existed.
 * 3.5 - PlanogramPegboardNonCircular - rename items to placements. Add pegProduct.
 * 3.4 - Add maxShelfHeight and maxProductHead to bays
 * 3.3 - Remove coordinates from bays and use width and height instead
 * 3.2 - Separate item squeezeDimension object
 * 3.1 - Add product ranking
 * 3.0 - PlanogramContainer label replaced with cdts, old data can't be upgraded correctly 100%
 * 2.15 - spaceMix in pogItem
 * 2.14 - squeeze dimensions support
 * 2.13 - unranged containers support
 * 2.12 - containers have sales information
 * 2.11 - add variantRestrictions to shelves
 * 2.10 - shelves ordered from top to bottom
 * 2.9 - add relaxedConstraints to Pog
 * 2.8 - transform from unranged item reason to reasons
 * 2.7 - removed hardcoded shelfNo and bayNo
 * 2.6 - unranged items have potential shelves
 * 2.5 - containers inside pog
 * 2.4 - dividerWidth support
 * 2.3 - grillHeight support
 * 2.2 - initial hangcells support
 * 2.1 - first pog with provided version, profit added to PlanogramItem, version added to Pog
 */

type PlanogramItemShelfRelaxtion =
  | "min_width"
  | "cdt0_contiguous"
  | "cdt1_contiguous"
  | "cdt2_contiguous"
  | "cdt3_contiguous"
  | "cdt1_order"
  | "cdt2_order"
  | "cdt3_order";
type PlanogramItemRelaxation =
  | "facings_not_enough"
  | "facings_too_many"
  | "units_not_enough_mos"
  | "units_not_enough_dos"
  | "units_too_many";

export type RelaxedItemConstraint = {
  relaxationType: PlanogramItemShelfRelaxtion | PlanogramItemRelaxation;
  entity: { itemId: string; bayNo: number; shelfNo: number };
  magnitude: number | "fully_relaxed";
};

export type RelaxedShelfConstraint = {
  relaxationType: PlanogramItemShelfRelaxtion | "min_row_fill_slack";
  /** [bay, shelf]. For `min_row_fill_slack`, magnitude is the gap beyond the tolerance (1/100 in). */
  entity: [number, number];
  magnitude: number | "fully_relaxed";
};

/**
 * Size tiers as shelf rows broken: the product and its cdt2, or the product, its [bay, shelf] and
 * which cdt3 rule inside the size tier row broke.
 */
export type RelaxedSizeTierConstraint =
  | {
      relaxationType: "cdt2_size_tier_rows";
      entity: [string, string[]];
      magnitude: "fully_relaxed";
    }
  | {
      relaxationType: "cdt3_size_tier_order";
      entity: [string, [number, number], "contiguous" | "order"];
      magnitude: "fully_relaxed";
    };

/** A release line order broken to keep each brand one block per bay (DEV-3531). */
export interface PogBrandOrderBreak {
  /** The brand of the line that should come first ("Native" in "Native before Old Spice"). */
  brandBefore: string;
  brandAfter: string;
  /** The cdt0 lines. */
  lineBefore: string;
  lineAfter: string;
  bayNo: number;
  lineBeforeOnShelves: number[];
  lineAfterOnShelves: number[];
}

export type RelaxedBrandOrderConstraint = {
  relaxationType: "brand_block_over_line_order";
  entity: PogBrandOrderBreak;
  magnitude: number | "fully_relaxed";
};

/**
 * The pogger's `meta.horizontal_rectangles` (DEV-3531), stored as it came back.
 * Lengths are the request's units (x100).
 */
export interface PogHorizontalRectangles {
  applied: boolean;
  /** Why a requested flag was not applied ("verticalBlockingMode"); null when applied. */
  ignoredBecause: string | null;
  /** Per bay number, how far its merged-row seam sits from the physical bay edge. */
  baySeamShift: Record<string, number>;
  mergedRows: {
    bayNo: number;
    shelfNo: number;
    /** The bay's share of the merged row. */
    width: number;
    physicalWidth: number;
    /** Width the bay leaves unused on the row; null when not measured. */
    gap: number | null;
    seamAfter: boolean;
  }[];
  overlapRules: number;
  overlapBreaks: {
    level: string;
    node: string[];
    upperRow: [number, number][];
    lowerRow: [number, number][];
    minOverlap: number;
    overlap: number | null;
  }[];
  /** "Each brand whole; this line order broken", also in `relaxedConstraints`. */
  brandOrderBreaks: PogBrandOrderBreak[];
}

/** Plain data: a deep copy, so a pog never shares the report with its source. */
export const clonePogHorizontalRectangles = (
  report: PogHorizontalRectangles,
): PogHorizontalRectangles => JSON.parse(JSON.stringify(report)) as PogHorizontalRectangles;

export type PlanogramRelaxConstraint =
  | RelaxedItemConstraint
  | RelaxedShelfConstraint
  | RelaxedSizeTierConstraint
  | RelaxedBrandOrderConstraint
  | {
      relaxationType: string;
      entity: string;
      magnitude: string;
    };

/** What `metrics.aesthetics` was read from; a null score means this version could not grade it. */
export interface AestheticsDetail {
  version: string;
  failedGates: AestheticsGateId[];
  /** 0-1 per graded component. */
  components: Partial<Record<ComponentKey, number>>;
  /** The CDT release it was graded against; null when it was graded on the plan's own columns.
   *  Absent when that is not known (a score stored before it was recorded): such a score cannot
   *  be matched to a release, so a reader grades the plan again rather than trust it. */
  cdtReleaseId?: number | null;
}

export const aestheticsDetailOf = (
  score: AestheticsScore | null,
  cdtReleaseId?: number | null,
): AestheticsDetail => {
  const release = cdtReleaseId === undefined ? {} : { cdtReleaseId };
  if (!score) return { version: AESTHETICS_VERSION, failedGates: [], components: {}, ...release };
  const components: Partial<Record<ComponentKey, number>> = {};
  for (const [key, component] of Object.entries(score.components) as [
    ComponentKey,
    ComponentScore,
  ][]) {
    if (component.graded) components[key] = component.score;
  }
  return {
    version: score.version,
    failedGates: score.gates
      .filter((gate) => gate.evaluated && !gate.passed)
      .map((gate) => gate.id),
    components,
    ...release,
  };
};

interface PogCommon {
  version: string;
  frontOverhangEnabled?: boolean;
  relaxedConstraints: PlanogramRelaxConstraint[];
  /** Absent unless a store run sent `horizontalRectangles` (DEV-3531). */
  horizontalRectangles?: PogHorizontalRectangles | null;
  /** The pogger's plan for a run with an aisle break; absent on every run without one. */
  splitPlan?: SplitPlan;
  metrics: {
    returnOnSpace: number | null;
    shelfAlignment: number | null;
    coreRange: number | null;
    dosMos: number | null;
    /** 0-100, or null when this plan has never been scored. */
    aesthetics: number | null;
    aestheticsDetail?: AestheticsDetail;
  };
}

export interface PogNonCircular extends PogCommon {
  containers: PlanogramContainerCommonNonCircular[];
  unrangedContainers: PlanogramContainerCommonNonCircular[];
  planogram: PlanogramNonCircular;
  unrangedItems: UnrangedPlanogramItemNonCircular[];
  deletedItems: (PlanogramItemCommonNonCircular & { storeCodes: string[] })[];
}

/** A placed `-UNIT` twin is stored under its tray's product code, so this one key covers both. */
const productKeyOf = (item: PlanogramItem) => item.productCode || item.uniqueId;

export class Pog implements PogCommon {
  version: string;
  frontOverhangEnabled?: boolean;
  relaxedConstraints: PlanogramRelaxConstraint[];
  horizontalRectangles?: PogHorizontalRectangles | null;
  splitPlan?: SplitPlan;
  metrics: {
    returnOnSpace: number | null;
    shelfAlignment: number | null;
    coreRange: number | null;
    dosMos: number | null;
    /** 0-100, or null when this plan has never been scored. */
    aesthetics: number | null;
    aestheticsDetail?: AestheticsDetail;
  };

  containers: PlanogramContainer[];
  unrangedContainers: UnrangedPlanogramContainer[];
  planogram: Planogram;
  unrangedItems: UnrangedPlanogramItem[];
  deletedItems: (PlanogramItemCommonNonCircular & { storeCodes: string[] })[];

  constructor(pog: PogNonCircular) {
    this.version = pog.version;
    this.frontOverhangEnabled =
      pog.frontOverhangEnabled ||
      pog.planogram.bays.some((bay) =>
        bay.shelves.some((shelf) => (shelf.maxFrontOverhang ?? 0) > 0),
      );
    this.relaxedConstraints = pog.relaxedConstraints.map((relaxedConstraint) => ({
      ...relaxedConstraint,
    }));
    if (pog.horizontalRectangles) {
      this.horizontalRectangles = clonePogHorizontalRectangles(pog.horizontalRectangles);
    }
    this.metrics = { ...pog.metrics, aesthetics: pog.metrics?.aesthetics ?? null };
    if (pog.splitPlan) {
      this.splitPlan = pog.splitPlan;
    }
    this.containers = pog.containers.map((container) => new PlanogramContainer(container, this));
    this.unrangedContainers = pog.unrangedContainers.map(
      (container) => new UnrangedPlanogramContainer(container, this),
    );
    this.planogram = new Planogram(pog.planogram, this);
    this.unrangedItems = pog.unrangedItems.map(
      (unrangedItem) => new UnrangedPlanogramItem(unrangedItem, this),
    );
    this.deletedItems = pog.deletedItems.map((item) =>
      normalizePlanogramItemPlacementDefaults(item),
    );
  }

  addContainer(container: PlanogramContainer) {
    this.containers.push(container);
    return container;
  }

  getBay = (bayId: string) => {
    return this.planogram.getBay(bayId);
  };

  getShelf = (shelfId: string) => {
    return this.planogram.getShelf(shelfId);
  };

  getItem = (itemId: string) => {
    const planogramItem = this.planogram.getItem(itemId);
    const unrangedPlanogramItem = this.getUnrangedItem(itemId);
    return planogramItem || unrangedPlanogramItem;
  };

  getItems = (itemIds: string[]) => {
    const items = [];

    for (const itemId of itemIds) {
      items.push(this.getItem(itemId));
    }

    return items;
  };

  getUnrangedItem = (itemId: string) => {
    return this.unrangedItems.find((item) => item.uniqueId === itemId) || null;
  };

  /**
   * `grading` is what a store pog is graded against, so the stored score matches its detail
   * page: the plan's CDT release `spine`, and the restrictions and core range its generation
   * request sent. A bare spine array is the release spine alone.
   */
  calculateMetrics = (grading?: readonly SpineLevel[] | AestheticsGrading | null) => {
    this.metrics = {
      ...this.metrics,
      returnOnSpace: this.calculateReturnOnSpace(),
      coreRange: this.calculateCoreRange(),
      shelfAlignment: this.calculateShelfAlignment(),
      dosMos: this.calculateDosMos(),
      ...this.scoreAesthetics(undefined, grading),
    };
  };

  /** Null score when there is no release spine and the plan's CDT columns name no block. Without
   *  sent restrictions the bands are the pog's stored fences, and without a sent core range the
   *  products the pog flags `inCoreRange` stand in. */
  scoreAesthetics = (
    planId?: string | number,
    grading?: readonly SpineLevel[] | AestheticsGrading | null,
  ) => {
    const score = releaseAestheticsScore(this, grading, planId);
    const { spine, releaseId } = gradingOf(grading);
    /* The release the grading names, even one with no levels (the plan is then graded on its own
       columns, as its page grades it). Without one, a spine's release is not known; no spine,
       none was used. */
    const cdtReleaseId = releaseId !== undefined ? releaseId : spine?.length ? undefined : null;
    return {
      aesthetics: score?.score ?? null,
      aestheticsDetail: aestheticsDetailOf(score, cdtReleaseId),
    };
  };

  calculateAesthetics = (
    planId?: string | number,
    grading?: readonly SpineLevel[] | AestheticsGrading | null,
  ) => this.scoreAesthetics(planId, grading).aesthetics;

  getRecommendedQuantities = () => {
    const recommendedQuantities: { [key: string]: number | undefined } = {};

    for (const bay of this.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          const previousRecommendedQuantity = recommendedQuantities[item.variant];

          if (
            previousRecommendedQuantity === undefined ||
            item.quantity < previousRecommendedQuantity
          ) {
            recommendedQuantities[item.variant] = item.quantity;
          }
        }
      }
    }

    return recommendedQuantities;
  };

  isHavingRangeRecommendations = () => {
    const recommendedQuantities = this.getRecommendedQuantities();

    for (const unrangedItem of this.unrangedItems) {
      const recommendedQuantity = recommendedQuantities[unrangedItem.variant];
      if (recommendedQuantity !== undefined && unrangedItem.quantity > recommendedQuantity) {
        return true;
      }
    }

    return false;
  };

  calculateDosMos = () => {
    let totalItems = 0;
    let itemsBelowDosMos = 0;

    const items = this.planogram.bays.flatMap((bay) => bay.shelves.flatMap((shelf) => shelf.items));
    // A tray and its `-UNIT` single share a product code and one demand, so their stock is pooled.
    const stockOf = new Map<string, number>();
    for (const item of items) {
      stockOf.set(productKeyOf(item), (stockOf.get(productKeyOf(item)) ?? 0) + item.getCapacity());
    }
    for (const item of items) {
      const stock = stockOf.get(productKeyOf(item)) ?? 0;
      const dos = (((stock / item.quantity) * 7) / item.minDaysOfSupply) * 100;
      const mos = (stock / item.minOnShelf) * 100;

      itemsBelowDosMos += dos < 100 || mos < 100 ? 1 : 0;
      totalItems++;
    }

    if (totalItems === 0 || isNaN(itemsBelowDosMos) || isNaN(totalItems)) {
      return null;
    }

    return (1 - itemsBelowDosMos / totalItems) * 100;
  };

  calculateShelfAlignment = () => {
    let totalBreakPoints = 0;
    let totalMisalignments = 0;
    const usedShelves: { [shelfId: string]: true } = {};

    for (let bayIndex = 0; bayIndex < this.planogram.bays.length; bayIndex++) {
      const bay = this.planogram.bays[bayIndex];
      const nextBay: PlanogramBay | undefined = this.planogram.bays[bayIndex + 1];
      if (!nextBay) break;

      shelfAlignmentChecker: for (const shelf of bay.shelves) {
        const shelfBottom = shelf.getYBottom();
        totalBreakPoints++;

        for (const nextBayShelf of nextBay.shelves) {
          // Shelf already used.
          if (usedShelves[nextBayShelf.uniqueId]) continue;

          // Shelf alignment found.
          if (nextBayShelf.getYBottom() === shelfBottom) {
            usedShelves[nextBayShelf.uniqueId] = true;
            continue shelfAlignmentChecker;
          }
        }

        // All next bay shelves checked, misalignment found.
        totalMisalignments++;
      }
    }

    if (totalBreakPoints === 0) {
      return null;
    }

    return (1 - totalMisalignments / totalBreakPoints) * 100;
  };

  calculateCoreRange = () => {
    const currentCoreRangeHash: Record<string, number> = {};
    const totalCoreRangeHash: Record<string, number> = {};

    this.planogram.bays.forEach((bay) => {
      bay.shelves.forEach((shelf) => {
        shelf.items.forEach((item) => {
          if (item.inCoreRange) {
            currentCoreRangeHash[item.productCode] = 1;
            totalCoreRangeHash[item.productCode] = 1;
          }
        });
      });
    });

    this.unrangedItems.forEach((unrangedItem) => {
      if (unrangedItem.inCoreRange) {
        totalCoreRangeHash[unrangedItem.productCode] = 1;
      }
    });

    const currentCoreRangeItems = Object.keys(currentCoreRangeHash).length;
    const totalCoreRangeItems = Object.keys(totalCoreRangeHash).length;

    if (
      totalCoreRangeItems === 0 ||
      Number.isNaN(currentCoreRangeItems) ||
      Number.isNaN(totalCoreRangeItems)
    ) {
      return null;
    }

    return (currentCoreRangeItems / totalCoreRangeItems) * 100;
  };

  /** Averaged over cdt0 and cdt1 so a starved group shows at either level; a single-group level
   *  carries no signal and is dropped unless both are single-group. */
  calculateReturnOnSpace = () => {
    const levels = [
      this.returnOnSpaceBy((item) => item.variant),
      this.returnOnSpaceBy((item) => item.cdt1),
    ];
    const telling = levels.filter((level) => level.groups > 1);
    const graded = telling.length ? telling : levels.slice(1);
    const score = graded.reduce((sum, level) => sum + level.score, 0) / graded.length;
    return isNaN(score) ? null : score;
  };

  private returnOnSpaceBy = (groupOf: (item: PlanogramItem) => string) => {
    const sumTotal = { width: 0, salesAmount: 0 };
    const sums: { [group: string]: { width: number; salesAmount: number } } = {};

    const items = this.planogram.bays.flatMap((bay) => bay.shelves.flatMap((shelf) => shelf.items));
    // Every placed item of a product carries the product's whole sales, so they share it.
    const placements = new Map<string, number>();
    for (const item of items) {
      placements.set(productKeyOf(item), (placements.get(productKeyOf(item)) ?? 0) + 1);
    }

    for (const item of items) {
      const group = groupOf(item);
      sums[group] ??= { width: 0, salesAmount: 0 };
      const itemSumWidthHorizontalFacings =
        item.getWidth() * Math.ceil(item.facings / item.facingsRows);
      const salesAmount = item.salesAmount / (placements.get(productKeyOf(item)) ?? 1);

      sums[group].width += itemSumWidthHorizontalFacings;
      sums[group].salesAmount += salesAmount;

      sumTotal.width += itemSumWidthHorizontalFacings;
      sumTotal.salesAmount += salesAmount;
    }

    let penalty = 0;

    for (const sum of Object.values(sums)) {
      const widthPercent = (sum.width / sumTotal.width) * 100;
      const salesAmountPercent = (sum.salesAmount / sumTotal.salesAmount) * 100;

      if (widthPercent < salesAmountPercent) {
        penalty += Math.abs(widthPercent - salesAmountPercent);
      }
    }

    return { score: 100 - penalty, groups: Object.keys(sums).length };
  };

  toNonCircular = () => {
    const pogNonCircular: PogNonCircular = {
      version: this.version,
      ...(this.frontOverhangEnabled ? { frontOverhangEnabled: true } : {}),
      planogram: {
        bays: this.planogram.bays.map((bay) => {
          const bayNonCircular: PlanogramBayNonCircular = {
            uniqueId: bay.uniqueId,
            width: bay.width,
            height: bay.height,
            depth: bay.depth,
            baseHeight: bay.baseHeight,
            notchOffset: bay.notchOffset,
            notchSpacing: bay.notchSpacing,
            maxNotch: bay.maxNotch,
            maxProductHead: bay.maxProductHead,
            maxShelfHeight: bay.maxShelfHeight,
            splitAfter: bay.splitAfter,
            ...(bay.pallet
              ? { pallet: { ...bay.pallet, accessSides: [...bay.pallet.accessSides] } }
              : {}),
            shelves: bay.shelves.map((shelf) => {
              const shelfNonCircular: PlanogramShelfNonCircular = {
                uniqueId: shelf.uniqueId,
                width: shelf.width,
                merchandisableWidth: shelf.merchandisableWidth,
                deadSpans: shelf.deadSpans.map((span) => ({ ...span })),
                weightCapacity: shelf.weightCapacity,
                effectiveWeightLimit: shelf.effectiveWeightLimit,
                ...(shelf.assembly ? { assembly: shelf.assembly } : {}),
                ...(shelf.standsOn ? { standsOn: shelf.standsOn } : {}),
                dividers: shelf.dividers ? { ...shelf.dividers } : null,
                dividersSetByHand: shelf.dividersSetByHand,
                dividersKeptFromFixture: shelf.dividersKeptFromFixture,
                loaderDepth: shelf.loaderDepth,
                loaderDecision: shelf.loaderDecision ? { ...shelf.loaderDecision } : null,
                depth: shelf.depth,
                maxFrontOverhang: shelf.maxFrontOverhang ?? 0,
                slope: shelf.slope ?? 0,
                thickness: shelf.thickness,
                offset: shelf.offset,
                notchNo: shelf.getNotchNo(),
                grillHeight: shelf.grillHeight,
                type: shelf.type,
                variantRestrictions: shelf.variantRestrictions.map((variantRestriction) => ({
                  ...variantRestriction,
                })),
                bay: shelf.bay.uniqueId,
                mergedLeft: shelf.mergedLeft?.uniqueId || null,
                mergedRight: shelf.mergedRight?.uniqueId || null,
                items: shelf.items.map((item) => {
                  const itemNonCircular: PlanogramItemNonCircular = {
                    ...adapterPlanogramItemToPlanogramItemCommon(item),
                    shelf: item.shelf.uniqueId,
                  };

                  return itemNonCircular;
                }),
                align: shelf.align || "between",
                ...(shelf.laneNo !== undefined ? { laneNo: shelf.laneNo } : {}),
              };

              return shelfNonCircular;
            }),

            pegboards: bay.pegboards.map((pegboard) => ({
              ...pegboard,
              bay: bay.uniqueId,
              placements: pegboard.placements.map((item) => ({
                ...item,
                pegboard: pegboard.uniqueId,
              })),
            })),
          };

          return bayNonCircular;
        }),
      },
      unrangedItems: this.unrangedItems.map((unrangedItem) => {
        const unrangedItemNonCircular: UnrangedPlanogramItemNonCircular = {
          ...adapterPlanogramItemToPlanogramItemCommon(unrangedItem),
          potentialShelves: [...unrangedItem.potentialShelves],
          reasons: [...unrangedItem.reasons],
          shelf: null,
        };

        return unrangedItemNonCircular;
      }),
      deletedItems: this.deletedItems.map((item) => ({ ...item, shelf: null })),
      containers: this.containers.map((container) => ({
        uniqueId: container.uniqueId,
        color: container.color,
        cdts: container.cdts.map((cdts) => ({ ...cdts })),
        xLeft: container.xLeft,
        xRight: container.xRight,
        yBottom: container.yBottom,
        yTop: container.yTop,
        shelfType: container.shelfType,
        sales: container.sales,
        minProductHeight: container.minProductHeight,
        maxProductHeight: container.maxProductHeight,
        algoCurrentTargetWidth: container.algoCurrentTargetWidth,
        algoMinTargetWidth: container.algoMinTargetWidth,
        algoTargetWidth: container.algoTargetWidth,
      })),
      unrangedContainers: this.unrangedContainers.map((unrangedContainer) => ({
        uniqueId: unrangedContainer.uniqueId,
        color: unrangedContainer.color,
        cdts: unrangedContainer.cdts.map((cdts) => ({ ...cdts })),
        xLeft: unrangedContainer.xLeft,
        xRight: unrangedContainer.xRight,
        yBottom: unrangedContainer.yBottom,
        yTop: unrangedContainer.yTop,
        shelfType: unrangedContainer.shelfType,
        sales: unrangedContainer.sales,
        minProductHeight: unrangedContainer.minProductHeight,
        maxProductHeight: unrangedContainer.maxProductHeight,
        algoCurrentTargetWidth: unrangedContainer.algoCurrentTargetWidth,
        algoMinTargetWidth: unrangedContainer.algoMinTargetWidth,
        algoTargetWidth: unrangedContainer.algoTargetWidth,
      })),
      relaxedConstraints: [...this.relaxedConstraints],
      ...(this.splitPlan ? { splitPlan: this.splitPlan } : {}),
      metrics: { ...this.metrics },
      // Only when there is one, so every other pog serialises as before.
      ...(this.horizontalRectangles
        ? { horizontalRectangles: clonePogHorizontalRectangles(this.horizontalRectangles) }
        : {}),
    };

    return pogNonCircular;
  };
}

export type TemplatePogThumbnail =
  | {
      status: "IN_PROGRESS" | "ERROR" | "NOT_STARTED";
      url?: never;
    }
  | {
      status: "DONE";
      url: string;
    };

export interface PogData {
  id: number | null;
  pog_data?: PogNonCircular | null;
  is_favourite: boolean;
  solution_id: number;
}
